import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  CalendarDays,
  MapPin,
  UserPlus,
  UserRound,
  Users,
  X,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";

import API_BASE, { withApiCredentials } from "../api";
import { trackProductEvent } from "../analytics/trackEvent";
import { getActiveAwareAuthToken } from "../auth/bootstrapTokens";
import DesktopRightRail from "../components/DesktopRightRail";
import "../styles/pages/SuggestedAnglers.css";

const DEFAULT_AVATAR = "https://cdn-icons-png.flaticon.com/512/847/847969.png";
const SUGGESTED_ANGLERS_PAGE_SIZE = 20;
const SUGGESTED_ANGLERS_MODE_HEADER = "X-Suggested-Anglers-Mode";
const SUGGESTED_ANGLERS_SNAPSHOT_HEADER = "X-Suggested-Anglers-Snapshot";
const SUGGESTED_ANGLERS_TOTAL_HEADER = "X-Suggested-Anglers-Total";

function normalizeId(value) {
  return value === null || value === undefined ? "" : String(value);
}

function getUserId(user) {
  return normalizeId(user?.id || user?.userId || user?.username);
}

function getAvatarSrc(user) {
  const avatar = String(user?.avatar || "").trim();
  if (!avatar) return DEFAULT_AVATAR;
  return /^https?:\/\//i.test(avatar) ? avatar : `${API_BASE}${avatar}`;
}

function humanizeValue(value) {
  return String(value || "")
    .replace(/^[^.]+\./, "")
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function translateValue(t, value) {
  const normalized = String(value || "").trim();
  if (!normalized) return "";
  return t(normalized, { defaultValue: humanizeValue(normalized) });
}

function formatLocation(user) {
  return [user?.city, user?.country]
    .map((part) => String(part || "").trim())
    .filter(Boolean)
    .join(", ");
}

function parseCreatedAt(value) {
  if (!value) return null;
  const numeric = Number(value);
  const date = Number.isFinite(numeric)
    ? new Date(numeric < 10000000000 ? numeric * 1000 : numeric)
    : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatJoinedDate(value, locale) {
  const date = parseCreatedAt(value);
  if (!date) return "";
  return date.toLocaleString(locale || undefined, {
    month: "short",
    year: "numeric",
  });
}

function getPreferenceChips(user, t) {
  const chipValues = [
    user?.experience,
    user?.fishingStyle,
    user?.waterType,
    user?.rangeType,
    ...(Array.isArray(user?.methods) ? user.methods : []),
    ...(Array.isArray(user?.favoriteSpecies) ? user.favoriteSpecies : []),
  ];

  const seen = new Set();
  return chipValues
    .map((value) => String(value || "").trim())
    .filter(Boolean)
    .map((value) => {
      const key = value.toLowerCase();
      if (seen.has(key)) return "";
      seen.add(key);
      return translateValue(t, value);
    })
    .filter(Boolean);
}

function buildRelationshipState(me) {
  return {
    following: new Set((me?.following || []).map(normalizeId)),
    friends: new Set((me?.friends || []).map(normalizeId)),
    sent: new Set((me?.friendRequestsSent || []).map(normalizeId)),
    received: new Set((me?.friendRequestsReceived || []).map(normalizeId)),
  };
}

function cloneRelationshipState(relationship) {
  return {
    following: new Set(relationship.following),
    friends: new Set(relationship.friends),
    sent: new Set(relationship.sent),
    received: new Set(relationship.received),
  };
}

function responseHeader(response, name) {
  return typeof response?.headers?.get === "function"
    ? String(response.headers.get(name) || "").trim()
    : "";
}

function parseSuggestionTotal(response, fallback) {
  const rawTotal = responseHeader(response, SUGGESTED_ANGLERS_TOTAL_HEADER);
  if (!rawTotal) return fallback;
  const parsed = Number(rawTotal);
  return Number.isInteger(parsed) && parsed >= 0 ? parsed : fallback;
}

function appendUniqueUsersById(currentUsers, incomingUsers) {
  const merged = Array.isArray(currentUsers) ? [...currentUsers] : [];
  const seenIds = new Set(merged.map(getUserId).filter(Boolean));

  (Array.isArray(incomingUsers) ? incomingUsers : []).forEach((user) => {
    const id = getUserId(user);
    if (id && seenIds.has(id)) return;
    if (id) seenIds.add(id);
    merged.push(user);
  });
  return merged;
}

async function requestSuggestedAnglersPage({ authToken, offset, snapshot }) {
  const params = new URLSearchParams({
    limit: String(SUGGESTED_ANGLERS_PAGE_SIZE),
    offset: String(offset),
  });
  if (snapshot) params.set("suggestion_snapshot", snapshot);

  const response = await fetch(
    `${API_BASE}/auth/suggested-anglers?${params.toString()}`,
    withApiCredentials({
      cache: "no-store",
      headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
    })
  );
  if (!response.ok) {
    const error = new Error("Failed to load suggested anglers");
    error.status = response.status;
    throw error;
  }

  const payload = await response.json();
  const users = Array.isArray(payload) ? payload : [];
  const mode =
    responseHeader(response, SUGGESTED_ANGLERS_MODE_HEADER) || "legacy";
  return {
    users,
    mode,
    snapshot: responseHeader(response, SUGGESTED_ANGLERS_SNAPSHOT_HEADER),
    total: parseSuggestionTotal(response, users.length),
  };
}

function notifySocialGraphUpdated() {
  window.dispatchEvent(new Event("friendsUpdated"));
  window.dispatchEvent(new Event("postsUpdated"));
}

export default function SuggestedAnglers({
  currentUser,
  users = [],
  posts = [],
  onOpenUser,
  enableDesktopRightRail = false,
  desktopRightRailTargetId,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [directoryUsers, setDirectoryUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [suggestionMode, setSuggestionMode] = useState("loading");
  const [suggestionSnapshot, setSuggestionSnapshot] = useState("");
  const [suggestionTotal, setSuggestionTotal] = useState(null);
  const [suggestionOffset, setSuggestionOffset] = useState(0);
  const [hasMoreSuggestions, setHasMoreSuggestions] = useState(false);
  const [loadingMoreSuggestions, setLoadingMoreSuggestions] = useState(false);
  const [relationship, setRelationship] = useState(() =>
    buildRelationshipState(currentUser)
  );
  const [relationshipLoading, setRelationshipLoading] = useState(false);
  const [pendingActions, setPendingActions] = useState({});
  const [errorMessage, setErrorMessage] = useState("");
  const [desktopRightRailTarget, setDesktopRightRailTarget] = useState(null);

  const authToken = getActiveAwareAuthToken(
    typeof window !== "undefined" ? window : undefined
  );
  const hasAuthenticatedUser = Boolean(currentUser?.id || currentUser?.username);

  useEffect(() => {
    if (!desktopRightRailTargetId || typeof document === "undefined") {
      setDesktopRightRailTarget(null);
      return;
    }

    setDesktopRightRailTarget(
      document.getElementById(desktopRightRailTargetId)
    );
  }, [desktopRightRailTargetId]);

  const closeUtilityPage = useCallback(() => {
    if (
      typeof window !== "undefined" &&
      window.history?.state &&
      Number(window.history.state.idx) > 0
    ) {
      navigate(-1);
      return;
    }

    navigate("/", { replace: true });
  }, [navigate]);

  useEffect(() => {
    let isMounted = true;
    setUsersLoading(true);
    setErrorMessage("");

    requestSuggestedAnglersPage({ authToken, offset: 0, snapshot: "" })
      .then((result) => {
        if (!isMounted) return;
        setDirectoryUsers(result.users);
        setSuggestionMode(result.mode);
        setSuggestionSnapshot(result.snapshot);
        setSuggestionTotal(result.total);
        setSuggestionOffset(result.users.length);
        setHasMoreSuggestions(
          result.mode === "personalized" &&
            Boolean(result.snapshot) &&
            result.users.length < result.total
        );
      })
      .catch(() => {
        if (isMounted) {
          setDirectoryUsers([]);
          setSuggestionMode("error");
          setSuggestionSnapshot("");
          setSuggestionTotal(null);
          setSuggestionOffset(0);
          setHasMoreSuggestions(false);
          setErrorMessage(
            t("suggested_anglers_load_error", {
              defaultValue: "Could not load suggested anglers.",
            })
          );
        }
      })
      .finally(() => {
        if (isMounted) setUsersLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [authToken, currentUser?.id, t]);

  const handleLoadMoreSuggestions = async () => {
    if (
      suggestionMode !== "personalized" ||
      !suggestionSnapshot ||
      loadingMoreSuggestions ||
      !hasMoreSuggestions
    ) {
      return;
    }

    setLoadingMoreSuggestions(true);
    setErrorMessage("");
    try {
      const result = await requestSuggestedAnglersPage({
        authToken,
        offset: suggestionOffset,
        snapshot: suggestionSnapshot,
      });
      if (result.mode !== "personalized" || !result.snapshot) {
        throw new Error("Personalized suggestion snapshot unavailable");
      }

      const nextOffset = suggestionOffset + result.users.length;
      setDirectoryUsers((currentUsers) =>
        appendUniqueUsersById(currentUsers, result.users)
      );
      setSuggestionSnapshot(result.snapshot);
      setSuggestionTotal(result.total);
      setSuggestionOffset(nextOffset);
      setHasMoreSuggestions(
        result.users.length > 0 && nextOffset < result.total
      );
    } catch {
      setSuggestionSnapshot("");
      setHasMoreSuggestions(false);
      setErrorMessage(
        t("suggested_anglers_snapshot_error", {
          defaultValue:
            "This suggestions session expired. Reopen the page to refresh it.",
        })
      );
    } finally {
      setLoadingMoreSuggestions(false);
    }
  };

  const refreshRelationshipState = useCallback(async () => {
    if (!authToken && !hasAuthenticatedUser) {
      setRelationship(buildRelationshipState(currentUser));
      return;
    }

    setRelationshipLoading(true);

    try {
      const res = await fetch(
        `${API_BASE}/auth/me`,
        withApiCredentials({
          headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
        })
      );

      if (!res.ok) throw new Error("Failed to load relationships");

      const me = await res.json();
      setRelationship(buildRelationshipState(me));
    } catch {
      setRelationship(buildRelationshipState(currentUser));
    } finally {
      setRelationshipLoading(false);
    }
  }, [authToken, currentUser, hasAuthenticatedUser]);

  useEffect(() => {
    refreshRelationshipState();
  }, [refreshRelationshipState]);

  const suggestedAnglers = useMemo(() => {
    const seen = new Set();
    const currentUsername = String(currentUser?.username || "").trim().toLowerCase();
    const currentId = getUserId(currentUser);

    return (directoryUsers || []).filter((candidate) => {
      const username = String(candidate?.username || "").trim();
      const normalizedUsername = username.toLowerCase();
      const id = getUserId(candidate);

      if (!username) return false;
      if (normalizedUsername === currentUsername) return false;
      if (id && currentId && id === currentId) return false;
      if (seen.has(normalizedUsername)) return false;

      seen.add(normalizedUsername);
      return true;
    });
  }, [currentUser, directoryUsers]);
  const displayedSuggestionTotal =
    suggestionMode === "personalized" && Number.isInteger(suggestionTotal)
      ? suggestionTotal
      : suggestedAnglers.length;

  const setPending = (key, value) => {
    setPendingActions((current) => ({
      ...current,
      [key]: value,
    }));
  };

  const runRelationshipMutation = async ({
    targetUser,
    actionKey,
    endpoint,
    applyOptimisticUpdate,
    buildFormData,
    successMessage,
    onSuccess,
  }) => {
    const targetId = getUserId(targetUser);

    if (!hasAuthenticatedUser || !targetId || pendingActions[actionKey]) {
      return;
    }

    const snapshot = cloneRelationshipState(relationship);
    setErrorMessage("");
    setPending(actionKey, true);
    setRelationship((current) => {
      const next = cloneRelationshipState(current);
      applyOptimisticUpdate(next, targetId);
      return next;
    });

    try {
      const formData = buildFormData(authToken, targetId);
      const res = await fetch(
        `${API_BASE}${endpoint}`,
        withApiCredentials({
          method: "POST",
          body: formData,
        })
      );

      if (!res.ok) throw new Error("Social action failed");

      notifySocialGraphUpdated();
      if (typeof onSuccess === "function") {
        onSuccess();
      }
      if (successMessage) setErrorMessage("");
    } catch {
      setRelationship(snapshot);
      setErrorMessage(
        t("suggested_anglers_action_error", {
          defaultValue: "That action could not be completed. Please try again.",
        })
      );
      refreshRelationshipState();
    } finally {
      setPending(actionKey, false);
    }
  };

  const buildTargetFormData = (token, targetId) => {
    const formData = new FormData();
    if (token) {
      formData.append("token", token);
    }
    formData.append("target_user_id", targetId);
    return formData;
  };

  const handleFollowToggle = (targetUser, shouldFollow) => {
    const targetId = getUserId(targetUser);
    runRelationshipMutation({
      targetUser,
      actionKey: `${targetId}:follow`,
      endpoint: shouldFollow ? "/follow" : "/follow/unfollow",
      buildFormData: buildTargetFormData,
      applyOptimisticUpdate: (next, id) => {
        if (shouldFollow) next.following.add(id);
        else next.following.delete(id);
      },
      onSuccess: shouldFollow
        ? () =>
            trackProductEvent("suggested_angler_follow_clicked", {
              featureArea: "suggested_anglers",
              metadata: { source: "suggested_anglers_card" },
            })
        : undefined,
    });
  };

  const handleFriendRequestToggle = (targetUser, shouldRequest) => {
    const targetId = getUserId(targetUser);
    runRelationshipMutation({
      targetUser,
      actionKey: `${targetId}:friend`,
      endpoint: shouldRequest ? "/friends/request" : "/friends/cancel",
      buildFormData: buildTargetFormData,
      applyOptimisticUpdate: (next, id) => {
        if (shouldRequest) next.sent.add(id);
        else next.sent.delete(id);
      },
      onSuccess: shouldRequest
        ? () =>
            trackProductEvent("friend_request_sent", {
              featureArea: "social",
              metadata: { source: "suggested_anglers_card" },
            })
        : undefined,
    });
  };

  const handleOpenSuggestedAnglerProfile = (username) => {
    trackProductEvent("suggested_angler_profile_opened", {
      featureArea: "suggested_anglers",
      metadata: { source: "suggested_anglers_card" },
    });
    onOpenUser?.(username);
  };

  const renderFriendButton = (angler) => {
    const id = getUserId(angler);
    const isPending = Boolean(pendingActions[`${id}:friend`]);

    if (relationship.friends.has(id)) {
      return (
        <button type="button" className="suggested-anglers-card__btn" disabled>
          <Users size={15} strokeWidth={2.2} aria-hidden="true" />
          <span>{t("friends", { defaultValue: "Friends" })}</span>
        </button>
      );
    }

    if (relationship.received.has(id)) {
      return (
        <button type="button" className="suggested-anglers-card__btn" disabled>
          <UserPlus size={15} strokeWidth={2.2} aria-hidden="true" />
          <span>
            {t("suggested_anglers_request_received", {
              defaultValue: "Request received",
            })}
          </span>
        </button>
      );
    }

    const hasSentRequest = relationship.sent.has(id);

    return (
      <button
        type="button"
        className="suggested-anglers-card__btn"
        disabled={isPending || !hasAuthenticatedUser}
        onClick={() => handleFriendRequestToggle(angler, !hasSentRequest)}
      >
        <UserPlus size={15} strokeWidth={2.2} aria-hidden="true" />
        <span>
          {hasSentRequest
            ? t("cancel_request", { defaultValue: "Cancel request" })
            : t("add_friend", { defaultValue: "Add friend" })}
        </span>
      </button>
    );
  };

  const desktopRightRail = enableDesktopRightRail ? (
    <DesktopRightRail
      currentUser={currentUser}
      posts={posts}
      users={users}
      onOpenUser={onOpenUser}
    />
  ) : null;

  return (
    <div
      className={`suggested-anglers-shell${
        enableDesktopRightRail ? " suggested-anglers-shell--with-right-rail" : ""
      }`}
    >
      <section className="suggested-anglers-page">
      <header className="suggested-anglers-header">
        <img
          src="/banner/sugested.png"
          alt=""
          aria-hidden="true"
          className="suggested-anglers-header__image suggested-anglers-header__image--light"
        />
        <img
          src="/banner/Darkmode/suggested-anglers-hero-dark.png"
          alt=""
          aria-hidden="true"
          className="suggested-anglers-header__image suggested-anglers-header__image--dark"
        />
        <div className="suggested-anglers-header__copy">
          <p className="suggested-anglers-kicker">
            {t("mobile_sidebar_explore_section", { defaultValue: "Explore" })}
          </p>
          <h1>
            {t("suggested_anglers", { defaultValue: "Suggested anglers" })}
          </h1>
          <p className="suggested-anglers-subtitle">
            {t("suggested_anglers_subtitle")}
          </p>
        </div>
        <span className="suggested-anglers-count" aria-hidden="true">
          <strong>{displayedSuggestionTotal}</strong>
          <Users size={42} strokeWidth={1.8} aria-hidden="true" />
        </span>
        <button
          type="button"
          className="suggested-anglers-close"
          onClick={closeUtilityPage}
          aria-label={t("common_close")}
        >
          <X size={18} strokeWidth={2.5} aria-hidden="true" />
        </button>
      </header>

      {errorMessage ? (
        <div className="suggested-anglers-alert">{errorMessage}</div>
      ) : null}

      {usersLoading || relationshipLoading ? (
        <div className="suggested-anglers-state">
          {t("suggested_anglers_loading", {
            defaultValue: "Loading suggested anglers...",
          })}
        </div>
      ) : suggestedAnglers.length === 0 ? (
        <div className="suggested-anglers-state">
          {t("suggested_anglers_empty", {
            defaultValue: "No suggested anglers yet.",
          })}
        </div>
      ) : (
        <div className="suggested-anglers-list">
          {suggestedAnglers.map((angler) => {
            const id = getUserId(angler);
            const username = String(angler?.username || "").trim();
            const displayName =
              angler?.displayName || angler?.name || username;
            const role =
              (angler?.accountType
                ? translateValue(t, angler.accountType)
                : "") ||
              angler?.role ||
              t("angler", { defaultValue: "Angler" });
            const location = formatLocation(angler);
            const joinedDate = formatJoinedDate(angler?.createdAt);
            const preferenceChips = getPreferenceChips(angler, t);
            const isFollowing = relationship.following.has(id);
            const isFollowPending = Boolean(pendingActions[`${id}:follow`]);
            const followAllowed =
              angler?.followPrivacy !== "nobody" &&
              (angler?.followPrivacy !== "friends" ||
                relationship.friends.has(id));

            return (
              <article className="suggested-anglers-card" key={id || username}>
                <div className="suggested-anglers-card__main">
                  <img
                    src={getAvatarSrc(angler)}
                    alt={t("profile_picture", { defaultValue: "Profile picture" })}
                    className="suggested-anglers-card__avatar"
                  />

                  <div className="suggested-anglers-card__body">
                    <div className="suggested-anglers-card__title-row">
                      <h2>
                        <button
                          type="button"
                          className="suggested-anglers-card__name-btn"
                          onClick={() => handleOpenSuggestedAnglerProfile(username)}
                        >
                          {displayName}
                        </button>
                      </h2>
                      <span className="suggested-anglers-card__role">{role}</span>
                    </div>

                    {location ? (
                      <p className="suggested-anglers-card__location">
                        <MapPin size={14} strokeWidth={2.2} aria-hidden="true" />
                        <span>{location}</span>
                      </p>
                    ) : null}

                    <div className="suggested-anglers-card__meta">
                      {joinedDate ? (
                        <span>
                          <CalendarDays size={13} strokeWidth={2.2} aria-hidden="true" />
                          {t("joined_date", {
                            defaultValue: "Joined {{date}}",
                            date: joinedDate,
                          })}
                        </span>
                      ) : null}
                      {Number.isFinite(Number(angler?.followersCount)) ? (
                        <span>
                          <Users size={13} strokeWidth={2.2} aria-hidden="true" />
                          {Number(angler.followersCount)}{" "}
                          {t("followers", { defaultValue: "followers" })}
                        </span>
                      ) : null}
                      {Number.isFinite(Number(angler?.friendsCount)) ? (
                        <span>
                          <UserRound size={13} strokeWidth={2.2} aria-hidden="true" />
                          {Number(angler.friendsCount)}{" "}
                          {t("friends", { defaultValue: "friends" })}
                        </span>
                      ) : null}
                    </div>
                  </div>
                </div>

                {preferenceChips.length > 0 ? (
                  <div className="suggested-anglers-card__chips">
                    {preferenceChips.map((chip) => (
                      <span key={chip}>{chip}</span>
                    ))}
                  </div>
                ) : null}

                <div className="suggested-anglers-card__actions">
                  <button
                    type="button"
                    className="suggested-anglers-card__btn suggested-anglers-card__btn--primary"
                    onClick={() => handleOpenSuggestedAnglerProfile(username)}
                  >
                    <UserRound size={15} strokeWidth={2.2} aria-hidden="true" />
                    <span>{t("view_profile", { defaultValue: "View profile" })}</span>
                  </button>

                  <button
                    type="button"
                    className="suggested-anglers-card__btn"
                    disabled={
                      isFollowPending ||
                      !hasAuthenticatedUser ||
                      !followAllowed
                    }
                    onClick={() => handleFollowToggle(angler, !isFollowing)}
                  >
                    <Users size={15} strokeWidth={2.2} aria-hidden="true" />
                    <span>
                      {isFollowing
                        ? t("unfollow", { defaultValue: "Unfollow" })
                        : t("follow", { defaultValue: "Follow" })}
                    </span>
                  </button>

                  {renderFriendButton(angler)}
                </div>
              </article>
            );
          })}
        </div>
      )}
      <div className="suggested-anglers-bottom-actions">
        {suggestionMode === "personalized" && hasMoreSuggestions ? (
          <button
            type="button"
            className="suggested-anglers-bottom-close"
            disabled={loadingMoreSuggestions}
            onClick={handleLoadMoreSuggestions}
          >
            {loadingMoreSuggestions
              ? t("suggested_anglers_loading_more", {
                  defaultValue: "Loading...",
                })
              : t("suggested_anglers_load_more", {
                  defaultValue: "Load more",
                })}
          </button>
        ) : null}
        <button
          type="button"
          className="suggested-anglers-bottom-close"
          onClick={closeUtilityPage}
        >
          {t("common_close")}
        </button>
      </div>
      </section>

      {desktopRightRail && desktopRightRailTarget
        ? createPortal(desktopRightRail, desktopRightRailTarget)
        : desktopRightRail && !desktopRightRailTargetId
          ? desktopRightRail
          : null}
    </div>
  );
}
